import React, { useState } from 'react';

export default function AdminAppDetailsSection({ app, appId, authFetch, showToast, onUpdate }) {
  const [isEditing, setIsEditing] = useState(false);
  const [editDescription, setEditDescription] = useState(app?.description || '');
  const [editDetails, setEditDetails] = useState(app?.details || '');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await authFetch('/api/apps/update', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: appId,
          description: editDescription.trim(),
          details: editDetails.trim() || null,
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to update app');
      }

      showToast('✅ App details updated', 'success');
      setIsEditing(false);
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error('Error updating app:', error);
      showToast(error.message || 'Failed to update app', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setEditDescription(app?.description || '');
    setEditDetails(app?.details || '');
    setIsEditing(false);
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <span>📝</span>
            <span>App Details</span>
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Description and feature details
          </p>
        </div>
        {!isEditing && (
          <button
            onClick={() => setIsEditing(true)}
            className="px-3 py-1.5 text-sm text-zapt-purple hover:text-zapt-purple/80 font-medium"
          >
            Edit
          </button>
        )}
      </div>

      {isEditing ? (
        <div className="space-y-4">
          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              value={editDescription}
              onChange={(e) => setEditDescription(e.target.value)}
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-zapt-purple/20 focus:border-zapt-purple resize-y"
              placeholder="Brief description of the app..."
            />
          </div>

          {/* Details */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Details / Features
            </label>
            <textarea
              value={editDetails}
              onChange={(e) => setEditDetails(e.target.value)}
              rows={8}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-zapt-purple/20 focus:border-zapt-purple resize-y font-mono"
              placeholder="Detailed features and requirements (supports markdown)..."
            />
            <p className="text-xs text-gray-500 mt-1">
              Supports markdown formatting
            </p>
          </div>

          {/* Actions */}
          <div className="flex gap-2 pt-2">
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-4 py-2 bg-zapt-purple text-white rounded-lg hover:bg-zapt-purple/90 font-medium text-sm disabled:opacity-50 flex items-center gap-2"
            >
              {saving && (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              )}
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
            <button
              onClick={handleCancel}
              disabled={saving}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-medium text-sm disabled:opacity-50"
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          {/* Description */}
          <div>
            <h3 className="text-sm font-medium text-gray-500 mb-1">Description</h3>
            {app?.description ? (
              <p className="text-gray-800 whitespace-pre-wrap">{app.description}</p>
            ) : (
              <p className="text-gray-400 italic">No description</p>
            )}
          </div>

          {/* Details */}
          {app?.details && (
            <div className="pt-4 border-t border-gray-100">
              <h3 className="text-sm font-medium text-gray-500 mb-2">Details / Features</h3>
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                <pre className="text-sm text-gray-800 whitespace-pre-wrap font-sans">{app.details}</pre>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
